import type { JSONSchema7 } from "json-schema";
import React, { memo, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";

import { CodeEditor } from "../../../../components/CMEditor/CodeEditor";
import { CustomModal } from "../../../../components/CustomModal";
import { TrashIcon } from "../../../../components/icons";
import { defaultResponseFormats } from "../../constants";
import type { ResponseFormatType } from "../../types";

interface SchemaModalProps {
  schema?: Record<string, unknown>;
  handleClose: () => void;
  handleSaveSchema: (schema: ResponseFormatType) => void;
  onCancel: () => void;
  usersFormats: any;
  removeUserFormat: (
    e: React.MouseEvent<HTMLButtonElement>,
    name: string,
  ) => void;
}

// const validationSchema: JSONSchema7 = {
//   type: "object",
//   properties: {
//     name: { type: "string", minLength: 1 },
//     description: { type: "string", minLength: 1 },
//     strict: { type: "boolean" },
//     parameters: {
//       type: "object",
//     },
//   },
//   required: ["name"],
//   additionalProperties: true,
// };

const SchemaModal: React.FC<SchemaModalProps> = memo(
  ({
    schema,
    handleClose,
    handleSaveSchema,
    onCancel,
    usersFormats,
    removeUserFormat,
  }) => {
    const [editorValue, setEditorValue] = useState(undefined);

    const [selectedExample, setSelectedExample] = useState<
      Record<string, unknown> | undefined
    >(schema);

    const [error, setError] = useState(false);

    const handleSave = () => {
      handleSaveSchema({
        type: "json_schema",
        json_schema: editorValue ? JSON.parse(editorValue) : selectedExample,
      });
    };

    const handleSelectSchema = (value: Record<string, unknown>) => {
      setSelectedExample(value);
      setError(false);
    };

    const handleEditTool = (value: any) => {
      setEditorValue(value);
      setError(false);
    };

    const handleError = (error: boolean) => {
      setError(error);
    };

    return (
      <CustomModal
        title={schema ? "Edit response format" : "Add response format"}
        show={true}
        handleClose={onCancel}
        size="lg"
        className="tools-modal"
        footer={
          <>
            <Button
              size="sm"
              variant="secondary"
              onClick={schema ? handleClose : onCancel}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={handleSave}
              disabled={error}
            >
              Save
            </Button>
          </>
        }
      >
        <p>
          Use a JSON schema to define the structure of the model&#39;s response
          format.
        </p>
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mt-3">Default</h6>
          <Dropdown className="select-tool-dropdown">
            <Dropdown.Toggle
              variant="outline-primary"
              className="btn-select-tool"
              size="sm"
            >
              <span className="text-truncate">
                {selectedExample ? `${selectedExample.name}` : "Examples"}
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Header>Default</Dropdown.Header>
              {defaultResponseFormats.map((schema: Record<string, unknown>) => (
                <Dropdown.Item
                  key={schema.name as string}
                  onClick={() => handleSelectSchema(schema)}
                >
                  <span>{schema.name as string}</span>
                </Dropdown.Item>
              ))}
              <Dropdown.Header>Custom</Dropdown.Header>
              {Object.keys(usersFormats).map(key => (
                <Dropdown.Item
                  key={key}
                  onClick={() => handleSelectSchema(usersFormats[key])}
                >
                  <span className="d-flex justify-content-between align-items-center w-100">
                    <span className="text-truncate">{key}</span>

                    <Button
                      variant="light"
                      className="btn-tool-control"
                      onClick={e => {
                        removeUserFormat(e, key);
                      }}
                    >
                      <TrashIcon />
                    </Button>
                  </span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="tools-editor-wrapper mt-4">
          <CodeEditor
            initialValue={JSON.stringify(selectedExample, null, 2)}
            onChange={handleEditTool}
            minHeight="340px"
            placeholder={JSON.stringify(
              defaultResponseFormats[0],
              null,
              2,
            ).slice(0, 300)}
            lineNumbers={false}
            onError={handleError}
            // validationSchema={validationSchema}
            autofocus
            withFooter
          />
        </div>
      </CustomModal>
    );
  },
);

export { SchemaModal };
